import * as ImagePicker from 'expo-image-picker';
import Analytics from '@/utils/analytics';
import CameraIcon from '@assets/icons/camera.svg';
import i18n from '@/utils/i18n';
import React, { useCallback, useState } from 'react';
import { ActivityIndicator } from '@/components/activity-indicator';
import { Button } from '@/components/button';
import { captureException } from '@/utils/sentry';
import { createStyles } from '@/contexts/theme-context';
import { Image, Platform, View } from 'react-native';
import { ImagePickerActionSheet } from '@/components/image-picker-action-sheet';
import { Review, SubmitFormPayload } from './hooks/use-review-form-reducer';
import { SCREEN_PADDING, shadow, TYPOGRAPHY } from '@/styles';
import { TextArea } from '@/components/text-area';
import { UPLOAD_PREFIXES, uploadPublicImage } from '@/services/s3';
import { useToasts } from '@/contexts/toast-context';

interface Props {
  initialReview: Review;
  onSubmit: (payload: SubmitFormPayload) => void;
  orderUUID: string;
}

export const ReviewForm: React.FC<Props> = ({
  orderUUID,
  onSubmit,
  initialReview,
}) => {
  const [styles, theme] = useStyles();

  const [comment, setComment] = useState(initialReview.comment || '');
  const [photoUrls, setPhotoUrls] = useState<string[]>(
    initialReview.photoUrlList || [],
  );
  const [showActionSheet, setShowActionSheet] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { showErrorToast } = useToasts();

  const uploadImage = useCallback(
    async (result: ImagePicker.ImagePickerResult) => {
      Analytics.track('order_item_review_form.image.select', { orderUUID });

      const imageURI = result.assets?.[0]?.uri;
      if (imageURI) {
        try {
          setShowActionSheet(false);
          setIsLoading(true);
          const key = await uploadPublicImage(
            orderUUID,
            imageURI,
            UPLOAD_PREFIXES.Orders,
          );
          setPhotoUrls([...photoUrls, key]);
        } catch (e) {
          showErrorToast();
          captureException(e);
        }
      }
      setIsLoading(false);
    },
    [orderUUID, photoUrls, showErrorToast],
  );

  return (
    <View style={styles.wrapper}>
      <View style={styles.formInputs}>
        <TextArea
          containerStyle={styles.textAreaContainer}
          focusedBorderColor={theme.colors.background}
          numberOfLines={4}
          onChangeText={setComment}
          placeholder={i18n.t('what_went_wrong')}
          style={TYPOGRAPHY.p}
          value={comment}
        />
        <View style={styles.imageRow}>
          {isLoading ? (
            <ActivityIndicator />
          ) : (
            photoUrls.map((imageUri, index) => (
              <View key={imageUri} style={styles.imageShadow}>
                <Image
                  alt={`review image ${index + 1} of ${photoUrls.length}`}
                  onError={(e) => captureException(e.nativeEvent.error)}
                  source={{ uri: imageUri }}
                  style={styles.image}
                  testID="review-test-id"
                />
              </View>
            ))
          )}
        </View>
      </View>
      <View style={styles.actions}>
        <Button
          containerStyle={styles.photoShadow}
          leftElement={<CameraIcon fill={theme.colors.primary} />}
          onPress={async () => {
            if (Platform.OS === 'web') {
              const result = await ImagePicker.launchImageLibraryAsync();
              uploadImage(result);
            } else {
              setShowActionSheet(true);
            }
          }}
          size="small"
          style={styles.photoButton}
          trackingData={{
            name: 'order_item_review_form.take_a_photo_button',
            properties: {
              orderUUID,
            },
          }}
          type="tertiary"
        >
          {Platform.OS === 'web'
            ? i18n.t('upload_image')
            : i18n.t('take_a_photo')}
        </Button>
        <Button
          onPress={() => {
            onSubmit({
              comment,
              photoUrls,
            });
          }}
          size="small"
          style={styles.submitButton}
          trackingData={{
            name: 'order_item_review_form.done_button',
            properties: {
              hasComment: Boolean(comment),
              hasPhotos: photoUrls.length > 0,
              orderUUID,
            },
          }}
          type="primary"
        >
          {i18n.t('continue')}
        </Button>
      </View>
      <ImagePickerActionSheet
        isVisible={showActionSheet}
        onClose={() => setShowActionSheet(false)}
        onSelect={uploadImage}
        title={i18n.t('attach_image')}
      />
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  actions: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 8,
    marginTop: 16,
  },
  formInputs: {
    backgroundColor: theme.colors.background,
    borderRadius: 8,
  },
  image: {
    height: 32,
    width: 24,
  },
  imageRow: {
    flexDirection: 'row',
    marginBottom: 12,
  },
  imageShadow: {
    ...shadow({
      color: theme.colors.black,
      elevation: 1,
      opacity: 0.15,
      radius: 4,
      x: 0,
      y: 2,
    }),
    borderRadius: 4,
    marginLeft: 8,
  },
  photoButton: {
    backgroundColor: theme.colors.white,
    borderRadius: 40,
    paddingHorizontal: 12,
  },
  photoShadow: {
    ...shadow({
      elevation: 8,
      opacity: 0.1,
      radius: 4,
      x: 0,
      y: 4,
    }),
  },
  submitButton: {
    borderRadius: 40,
    paddingHorizontal: 16,
  },
  textAreaContainer: {
    backgroundColor: theme.colors.background,
    borderColor: theme.colors.background,
  },
  wrapper: {
    marginHorizontal: SCREEN_PADDING,
  },
}));
